import React, { useState, useRef } from "react";
import { GoogleGenerativeAI, Part } from "@google/generative-ai";
import { ChevronLeft, ChevronRight, Upload, FileText } from "lucide-react";

// Type definitions
interface Flashcard {
  front: string;
  back: string;
}

declare global {
  interface Window {
    claude?: {
      complete: (prompt: string) => Promise<string>;
    };
    google?: {
      generativeAI: {
        GenerativeModel: new (modelName: string, apiKey: string) => any;
      };
    };
    "pdfjs-dist"?: any;
  }
}

const FlashcardApp = () => {
  const [mode, setMode] = useState<"create" | "loading" | "study">("create");
  const [topic, setTopic] = useState("");
  const [flashcards, setFlashcards] = useState<Flashcard[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [activeTab, setActiveTab] = useState<"paste" | "describe" | "upload">(
    "describe"
  );
  const [animating, setAnimating] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Preload PDF.js for better reliability
  React.useEffect(() => {
    console.log("🚀 Preloading dependencies...");

    // Preload PDF.js
    if (!window["pdfjs-dist"]) {
      console.log("📦 Preloading PDF.js...");
      const script = document.createElement("script");
      script.src = "https://unpkg.com/pdfjs-dist@4.0.379/build/pdf.min.js";
      script.async = true;
      document.head.appendChild(script);
    }
  }, []);

  // Helper function to convert a File object to a GoogleGenerativeAI.Part object.
  async function fileToGenerativePart(file: File): Promise<Part> {
    const base64EncodedData = await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve((reader.result as string).split(",")[1]);
      reader.readAsDataURL(file);
    });
    return {
      inlineData: {
        data: base64EncodedData,
        mimeType: file.type,
      },
    };
  }

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    console.log("📁 File upload started...");
    const file = event.target.files?.[0];
    if (!file) {
      console.log("❌ No file selected");
      return;
    }

    console.log("📄 File details:", {
      name: file.name,
      size: file.size,
      type: file.type,
    });

    if (file.type !== "application/pdf") {
      console.error("❌ Invalid file type:", file.type);
      alert("Please upload a PDF file");
      return;
    }

    console.log("✅ Valid PDF file selected");
    setUploadedFile(file);
    setActiveTab("upload");
    // We no longer need to extract text on the client side.
    // The file will be sent directly to the Gemini API.
  };

  const generateFlashcards = async () => {
    if (activeTab !== "upload" && !topic.trim()) return;
    if (activeTab === "upload" && !uploadedFile) {
      alert("Please upload a PDF file first.");
      return;
    }

    setMode("loading");

    try {
      console.log("🚀 Starting flashcard generation...");
      console.log("📝 Active tab:", activeTab);

      const apiKey = import.meta.env.VITE_GEMINI_API_KEY;

      if (!apiKey || apiKey === "YOUR_API_KEY") {
        console.error("❌ API key not configured");
        throw new Error(
          "Please set your Gemini API key in the .env file (VITE_GEMINI_API_KEY)."
        );
      }
      console.log("🔑 API key found.");

      console.log("🤖 Creating Gemini client and model...");
      const genAI = new GoogleGenerativeAI(apiKey);
      const model = genAI.getGenerativeModel({ model: "gemini-1.5-flash" });
      console.log("✅ Gemini client and model created successfully");

      let prompt: string;
      let requestParts: (string | Part)[];

      if (activeTab === "upload" && uploadedFile) {
        console.log("📄 Preparing PDF file for API...");
        const filePart = await fileToGenerativePart(uploadedFile);
        prompt = `You are an expert at creating educational materials. Analyze the content of this PDF file and generate between 5 and 15 high-quality flashcards.

        Guidelines:
        1. Identify the most important terms, concepts, or key facts in the document.
        2. For each flashcard, create a concise "front" (a term or question) and a clear, brief "back" (the definition or answer).
        3. Ensure the flashcards are accurate and reflect the core information of the PDF.

        Respond ONLY with a valid JSON array of flashcard objects in this exact format:
        [
          { "front": "Term/Question", "back": "Definition/Answer" }
        ]
        
        Do not include any text outside the JSON array.`;
        requestParts = [prompt, filePart];
        console.log("✅ PDF file ready.");
      } else {
        console.log("📄 Preparing text-based prompt...");
        prompt = `You are tasked with creating educational flashcards about "${topic}". Your goal is to create concise, clear, and accurate flashcard pairs that would help someone learn this topic.

        Guidelines for creating effective flashcards:
        1. Each flashcard should have a clear term/concept on one side and a concise definition/explanation on the other
        2. Terms should be specific and focused (ideally 1-5 words)
        3. Definitions should be clear and brief (ideally under 50 words)
        4. Focus on the most important concepts related to the topic
        5. Make the content educational, accurate, and helpful for learning

        Please provide exactly 10 flashcards in this JSON format - don't include any text outside the JSON:
        [
          { "front": "Term or concept", "back": "Definition or explanation" }
        ]`;
        requestParts = [prompt];
        console.log("✅ Text prompt ready.");
      }

      console.log("📤 Sending prompt and file (if any) to Gemini...");
      const result = await model.generateContent(requestParts);
      console.log("📥 Received response from Gemini");

      const response = await result.response;
      const responseText = response.text();
      console.log(
        "📄 Response preview:",
        responseText.substring(0, 200) + "..."
      );

      console.log("🧹 Cleaning up API response...");
      const cleanedResponseText = responseText
        .replace(/```json|```/g, "")
        .trim();

      console.log("🔍 Parsing JSON response...");
      const cards = JSON.parse(cleanedResponseText);
      console.log("✅ JSON parsed successfully, cards count:", cards.length);

      if (!Array.isArray(cards)) {
        throw new Error("Response is not an array of flashcards");
      }

      console.log("📚 Setting flashcards in state...");
      setFlashcards(cards);
      setCurrentIndex(0);
      setFlipped(false);
      setMode("study");
      console.log("🎉 Flashcard generation completed successfully!");
    } catch (error: unknown) {
      console.error("❌ Error generating flashcards:", error);

      const errorObj = error as Error;
      console.error("🔍 Error details:", {
        name: errorObj.name,
        message: errorObj.message,
        stack: errorObj.stack,
      });

      let errorMessage = "Failed to generate flashcards. Please try again.";

      if (errorObj.message.includes("API key")) {
        errorMessage =
          "API key error. Please check if your API key is valid and has access to the Gemini API.";
      } else if (errorObj.message.includes("400")) {
        errorMessage =
          "The uploaded file may be corrupted or in a format the AI cannot process. Please try a different file.";
      } else if (errorObj.message.includes("JSON")) {
        errorMessage = "Invalid response from AI service. Please try again.";
      }

      alert(errorMessage);
      setMode("create");
    }
  };

  const handleFlip = React.useCallback(() => {
    setFlipped((f) => !f);
  }, []);

  const handleNext = React.useCallback(() => {
    if (currentIndex < flashcards.length - 1 && !animating) {
      setAnimating(true);
      setTimeout(() => {
        setFlipped(false);
        setCurrentIndex((c) => c + 1);
        setTimeout(() => setAnimating(false), 50);
      }, 150);
    }
  }, [currentIndex, flashcards.length, animating]);

  const handlePrevious = React.useCallback(() => {
    if (currentIndex > 0 && !animating) {
      setAnimating(true);
      setTimeout(() => {
        setFlipped(false);
        setCurrentIndex((c) => c - 1);
        setTimeout(() => setAnimating(false), 50);
      }, 150);
    }
  }, [currentIndex, animating]);

  // Keyboard event handling for study mode
  React.useEffect(() => {
    if (mode !== "study") return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        e.stopPropagation();
        handlePrevious();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        e.stopPropagation();
        handleNext();
      } else if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        e.preventDefault();
        e.stopPropagation();
        handleFlip();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [mode, handlePrevious, handleNext, handleFlip]);

  if (mode === "create") {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: "#8B7355" }}
      >
        <div className="w-full max-w-lg p-8">
          <h1 className="text-white text-4xl font-bold text-center mb-8">
            Create flashcards
          </h1>

          <div className="flex justify-center mb-6">
            <div className="bg-white/20 p-0.5 rounded-full inline-flex">
              <button
                onClick={() => setActiveTab("paste")}
                className={`px-6 py-2 rounded-full font-medium transition-all text-base ${
                  activeTab === "paste"
                    ? "bg-white text-gray-700 shadow-md"
                    : "text-white hover:text-white/90"
                }`}
              >
                Paste text
              </button>
              <button
                onClick={() => setActiveTab("describe")}
                className={`px-6 py-2 rounded-full font-medium transition-all text-base ${
                  activeTab === "describe"
                    ? "bg-white text-gray-700 shadow-md"
                    : "text-white hover:text-white/90"
                }`}
              >
                Describe topic
              </button>
              <button
                onClick={() => setActiveTab("upload")}
                className={`px-6 py-2 rounded-full font-medium transition-all text-base ${
                  activeTab === "upload"
                    ? "bg-white text-gray-700 shadow-md"
                    : "text-white hover:text-white/90"
                }`}
              >
                Upload PDF
              </button>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-8 shadow-lg">
            {activeTab === "upload" ? (
              <div className="text-center">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                {!uploadedFile ? (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-300 rounded-2xl p-8 cursor-pointer hover:border-gray-400 transition-colors"
                  >
                    <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <p className="text-gray-600 mb-2">
                      Click to upload a PDF file
                    </p>
                    <p className="text-gray-400 text-sm">or drag and drop</p>
                  </div>
                ) : (
                  <div className="text-left">
                    <div className="flex items-center mb-4">
                      <FileText className="h-6 w-6 text-blue-500 mr-2" />
                      <span className="text-gray-700 font-medium">
                        {uploadedFile.name}
                      </span>
                    </div>
                    <p className="text-gray-600">
                      Your PDF is ready to be sent to the AI for processing.
                    </p>
                    <button
                      onClick={() => {
                        setUploadedFile(null);
                        if (fileInputRef.current) {
                          fileInputRef.current.value = "";
                        }
                      }}
                      className="mt-3 text-blue-500 hover:text-blue-600 underline"
                    >
                      Upload a different file
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <textarea
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder={
                  activeTab === "describe"
                    ? "Describe a topic and this will generate the details...\n\ne.g. capitals of the world\ne.g. fun facts about San Diego"
                    : "Paste your text here..."
                }
                className="w-full h-52 text-gray-900 placeholder-gray-400 resize-none focus:outline-none"
                style={{ fontSize: "18px", lineHeight: "1.6" }}
              />
            )}
          </div>

          <button
            onClick={generateFlashcards}
            className={`w-full mt-8 py-4 font-medium rounded-full transition-colors text-lg bg-gray-900 text-white hover:bg-gray-800`}
          >
            Generate flashcards
          </button>
        </div>
      </div>
    );
  }

  if (mode === "loading") {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: "#8B7355" }}
      >
        <div className="text-center">
          <h1 className="text-white text-4xl font-medium mb-4">
            Generating your flashcard set...
          </h1>
          <p className="text-white/80 mb-8">
            This may take a while depending on the content...
          </p>
          <div className="flex justify-center">
            <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
          </div>
        </div>
      </div>
    );
  }

  if (mode === "study") {
    const currentCard = flashcards[currentIndex];

    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: "#8B7355" }}
      >
        <div className="w-full max-w-2xl px-8">
          <div className="relative" style={{ perspective: "1000px" }}>
            <div
              className={`relative w-full h-96 transition-all duration-700 cursor-pointer ${
                flipped ? "[transform:rotateX(180deg)]" : ""
              } ${animating ? "scale-95 opacity-0" : "scale-100 opacity-100"}`}
              onClick={handleFlip}
              style={{ transformStyle: "preserve-3d" }}
            >
              {/* Front of card */}
              <div
                className="absolute inset-0 bg-white rounded-3xl shadow-xl flex flex-col items-center justify-center p-8"
                style={{ backfaceVisibility: "hidden" }}
              >
                <div className="text-center flex-1 flex items-center justify-center">
                  <h2 className="text-4xl font-medium text-gray-800">
                    {currentCard.front}
                  </h2>
                </div>
                <p className="text-gray-500 mt-auto">
                  Use ↑↓ arrows or click to flip
                </p>
              </div>

              {/* Back of card */}
              <div
                className="absolute inset-0 bg-white rounded-3xl shadow-xl flex items-center justify-center p-8"
                style={{
                  backfaceVisibility: "hidden",
                  transform: "rotateX(180deg)",
                }}
              >
                <div className="text-center">
                  <p className="text-xl text-gray-700 leading-relaxed">
                    {currentCard.back}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-center mt-8">
            <button
              onClick={handlePrevious}
              disabled={currentIndex === 0}
              className={`p-3 rounded-full transition-all ${
                currentIndex === 0
                  ? "bg-white/20 text-white/50 cursor-not-allowed"
                  : "bg-white/20 text-white hover:bg-white/30"
              }`}
            >
              <ChevronLeft size={24} />
            </button>

            <span className="mx-6 text-white text-lg font-medium">
              {currentIndex + 1} / {flashcards.length}
            </span>

            <button
              onClick={handleNext}
              disabled={currentIndex === flashcards.length - 1}
              className={`p-3 rounded-full transition-all ${
                currentIndex === flashcards.length - 1
                  ? "bg-white/20 text-white/50 cursor-not-allowed"
                  : "bg-white/20 text-white hover:bg-white/30"
              }`}
            >
              <ChevronRight size={24} />
            </button>
          </div>

          <div className="text-center mt-6">
            <button
              onClick={() => {
                setMode("create");
                setTopic("");
                setFlashcards([]);
                setUploadedFile(null);
              }}
              className="text-white/70 hover:text-white transition-colors underline-offset-2 hover:underline"
            >
              Create new flashcards
            </button>
          </div>
        </div>
      </div>
    );
  }
};

export default FlashcardApp;
